import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Stack,
    Typography,
    Textarea,
    Avatar,
} from "@mui/joy";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import { SmilePlus } from "lucide-react";
import moment from "moment";
import { useNotes } from "../../hooks/query";

interface CommentsProps {
    entityType?: string;
    entityId?: string;
}

export default function Comments({ entityType, entityId }: CommentsProps) {
    const [comment, setComment] = useState("");
    const [comments, setComments] = useState<
        { text: string; file?: string; author?: string; date?: string }[]
    >([]);
    const [file, setFile] = useState<File | null>(null);

    const { useList, useCreate } = useNotes();
    const listSvc = useList();
    const createSvc = useCreate();

    const fetchComments = async () => {
        if (!entityType || !entityId) return;
        try {
            const result = await listSvc.mutateAsync({
                EntityType: entityType,
                EntityId: entityId,
            });
            const mapped = result.map((note: any) => ({
                text: note.Content,
                author: note.CreatedByName || "User",
                date: note.CreatedAt
                    ? moment(note.CreatedAt).fromNow()
                    : "Just now",
            }));
            setComments(mapped);
        } catch (e) {
            console.error("Failed to fetch comments:", e);
        }
    };

    useEffect(() => {
        if (entityType && entityId) {
            fetchComments();
        }
    }, [entityType, entityId]);

    const handleCommentSubmit = async () => {
        if (!comment.trim() && !file) return;

        if (entityType && entityId) {
            try {
                await createSvc.mutateAsync({
                    EntityType: entityType,
                    EntityId: entityId,
                    Content: comment,
                });
                setComment("");
                setFile(null);
                fetchComments();
            } catch (e) {
                console.error("Failed to save comment:", e);
            }
        } else {
            setComments([
                ...comments,
                {
                    text: comment,
                    file: file?.name,
                    author: "You",
                    date: "Just now",
                },
            ]);
            setComment("");
            setFile(null);
        }
    };

    return (
        <Box sx={{ mx: "auto" }}>
            {/* Comment Input Section */}
            <Box sx={{ position: "relative" }} mb={2}>
                <Textarea
                    variant="outlined"
                    sx={{
                        borderRadius: 0,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.surface",
                        pb: 5,
                        "&:focus-within": {
                            borderColor: "primary.500",
                            boxShadow: "none",
                        },
                    }}
                    placeholder="Add inspection notes..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    minRows={3}
                />
            </Box>

            <Box mb={2} display={"flex"} gap={1}>
                <Button
                    onClick={handleCommentSubmit}
                    variant="solid"
                    color="primary"
                    size="sm"
                    sx={{ borderRadius: 0 }}
                >
                    Save
                </Button>
                <Button
                    onClick={handleCommentSubmit}
                    variant="plain"
                    color="neutral"
                    size="sm"
                    sx={{ borderRadius: 0 }}
                >
                    Cancel
                </Button>
            </Box>
            {/* Comments List */}
            <Stack spacing={1.5}>
                {comments.map((c, index) => {
                    const initials = c.author
                        ? c.author
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)
                        : "?";
                    return (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                gap: 1.5,
                                py: 1, // small vertical padding instead of all-around padding with border
                            }}
                        >
                            <Avatar
                                variant="soft"
                                color="neutral"
                                sx={{
                                    width: 36,
                                    height: 36,
                                    fontSize: "sm",
                                    fontWeight: 600,
                                    borderRadius: "50%",
                                }}
                            >
                                {initials}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography
                                        level="title-sm"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {c.author}
                                    </Typography>
                                    <Typography
                                        level="body-xs"
                                        sx={{ color: "text.tertiary" }}
                                    >
                                        {c.date}
                                    </Typography>
                                </Box>
                                <Typography
                                    level="body-sm"
                                    sx={{
                                        color: "text.primary",
                                        whiteSpace: "pre-wrap",
                                        mb: 1.5,
                                    }}
                                >
                                    {c.text}
                                </Typography>
                                {c.file && (
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            color: "text.secondary",
                                            bgcolor: "background.level1",
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 0,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            mb: 1.5,
                                        }}
                                    >
                                        📎 {c.file}
                                    </Typography>
                                )}

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            color: "text.secondary",
                                            cursor: "pointer",
                                            "&:hover": {
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        Edit
                                    </Typography>
                                    <Typography
                                        level="body-xs"
                                        sx={{ color: "text.secondary" }}
                                    >
                                        ·
                                    </Typography>
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            color: "text.secondary",
                                            cursor: "pointer",
                                            "&:hover": {
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        Delete
                                    </Typography>
                                    <Typography
                                        level="body-xs"
                                        sx={{ color: "text.secondary" }}
                                    >
                                        ·
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            color: "text.secondary",
                                            cursor: "pointer",
                                            "&:hover": {
                                                color: "text.primary",
                                            },
                                        }}
                                    >
                                        <SmilePlus
                                            size={14}
                                            strokeWidth={2.5}
                                        />
                                    </Box>
                                    <Typography
                                        level="body-xs"
                                        sx={{ color: "text.secondary" }}
                                    >
                                        ·
                                    </Typography>
                                    <Typography
                                        level="body-xs"
                                        sx={{ color: "text.secondary" }}
                                    >
                                        0 Replies
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}
